import React, { useEffect, useState } from 'react';
import { gradingConfig, subscribeGradingConfig, GRADING_DEFAULTS } from '../utils/gradingDefaults';
import { saveGradingConfig } from '../data/gradingConfigSync';

const clone = (o) => JSON.parse(JSON.stringify(o));

// [key, label, min, max, step]
const FIELDS = {
  ceil: [
    ['subjectEv', 'Subject lift — EV cap', 0, 2, 0.05],
    ['subjectAmt', 'Subject lift — strength cap', 0, 1, 0.05],
    ['waterSat', 'Water teal — sat cap', 0, 40, 1],
    ['waterAmt', 'Water teal — strength cap', 0, 1, 0.05],
    ['vibrance', 'Vibrance cap', 0, 100, 1],
    ['saturation', 'Saturation cap', 0, 100, 1],
    ['dehaze', 'Dehaze cap', 0, 100, 1],
    ['clarity', 'Clarity cap', 0, 100, 1],
    ['hslSat', 'HSL sat cap (±)', 0, 100, 1],
    ['wheelSat', 'Wheel sat cap (±)', 0, 100, 1],
  ],
  autoMask: [
    ['subjectLiftCeil', 'Subject lift — generated EV ceiling', 0, 2, 0.05],
    ['subjectAmount', 'Subject lift — generated strength', 0, 1, 0.05],
    ['subjectLiftTarget', 'Subject lift — target lightness (L)', 30, 80, 1],
    ['subjectLiftDiv', 'Subject lift — softness (bigger = gentler)', 10, 80, 1],
    ['waterSatBase', 'Water teal — generated base', 0, 40, 1],
    ['waterSatMax', 'Water teal — generated max', 0, 40, 1],
    ['waterAmount', 'Water teal — generated strength', 0, 1, 0.05],
    ['minConfidence', 'Skip auto masks below seg confidence (0=off)', 0, 1, 0.01],
  ],
  pipeline: [
    ['stillsConcurrency', 'Always-on worker — bake slots', 1, 16, 1],
    ['jobMaxConcurrency', 'Global Fargate task cap', 1, 200, 1],
    ['stillsParallel', 'Max runners per clip bake', 1, 200, 1],
    ['stillsRunnerConc', 'Frames per runner (bake width)', 1, 12, 1],
    ['drainThreshold', 'Loose backlog before drain fan-out', 1, 200, 1],
    ['drainCap', 'Max drain runners', 1, 100, 1],
    ['framesPerChunk', 'RED decode — frames per chunk', 30, 600, 10],
    ['maxChunks', 'RED decode — max parallel chunks', 1, 64, 1],
  ],
  selfAssess: [
    ['satMeanUp', 'Saturation drift cap (over neutral)', 0, 1, 0.01],
    ['satP90Up', 'Highlight saturation drift cap', 0, 1, 0.01],
    ['tealUp', 'Teal/blue cast drift cap', 0, 1, 0.01],
    ['castMag', 'Any colour-cast drift cap', 0, 1, 0.01],
    ['detailRatioMin', 'Min detail kept vs neutral (0–1)', 0.5, 1, 0.01],
    ['whiteRBLo', 'Foam white — R/B floor', 0.85, 1.05, 0.01],
    ['whiteRBHi', 'Foam white — R/B ceiling', 0.95, 1.2, 0.01],
    ['meanLumaUp', 'Brightness lift cap over neutral', 0, 0.5, 0.01],
  ],
};

export default function GradingConfigPanel({ onClose }) {
  const [draft, setDraft] = useState(() => clone(gradingConfig()));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  // If someone else saves while open, fold their values into anything untouched.
  useEffect(() => subscribeGradingConfig((c) => setDraft((d) => ({ ...clone(c), ...d }))), []);

  const setVal = (group, key, v) => {
    setMsg('');
    setDraft((d) => ({ ...d, [group]: { ...d[group], [key]: v } }));
  };
  const save = async () => {
    setBusy(true);
    try { await saveGradingConfig({ ceil: draft.ceil, autoMask: draft.autoMask, pipeline: draft.pipeline, selfAssess: draft.selfAssess }); setMsg('Saved — live everywhere in ~a minute, no deploy.'); }
    catch (e) { setMsg(`Save failed: ${e.message}`); }
    setBusy(false);
  };
  const reset = () => { setDraft(clone(GRADING_DEFAULTS)); setMsg('Reset to defaults (not yet saved).'); };

  const group = (name, title, note) => (
    <div className="cs-cfg-group">
      <div className="cs-cfg-grouphd">{title}</div>
      <div className="cs-cfg-note">{note}</div>
      <div className="cs-cfg-grid">
        {FIELDS[name].map(([key, label, min, max, step]) => (
          <label key={key} className="cs-cfg-field">
            <span className="cs-cfg-label">{label}</span>
            <input
              type="number" min={min} max={max} step={step}
              value={draft[name]?.[key] ?? ''}
              onChange={(e) => setVal(name, key, e.target.value === '' ? '' : Number(e.target.value))}
            />
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <div className="cs-cfg-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cs-cfg-modal">
        <div className="cs-cfg-head">
          <div className="cs-cfg-title">Auto-grade defaults (live)</div>
          <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={onClose}>✕</button>
        </div>
        <div className="cs-cfg-sub">
          Stored in Firestore (<code>config/grading</code>) — changes apply to the editor preview/reference and all new bakes with <b>no deploy</b>. Caps clamp the final look; generated values set what the per-frame masks start at.
        </div>
        <div className="cs-cfg-body">
          {group('ceil', 'Intensity caps', 'Hard ceilings every auto-grade is clamped to. The two big levers are Subject lift EV/strength and Water teal sat/strength.')}
          {group('autoMask', 'Mask generation', 'How the per-frame perception masks are built before the caps apply.')}
          {group('pipeline', 'Bake throughput / fan-out', 'Live concurrency knobs for the bake pipeline. Bigger = more parallel Fargate tasks (faster, more $). Applies to new bakes within ~a minute.')}
          {group('selfAssess', 'Self-assessment (natural look)', 'The grading philosophy as rules: the plain medium-delog decode is the natural target. These cap how far an auto-graded frame may drift from it before the critique loop must pull back (over-saturated, teal/blue cast, lost wave detail, off-neutral whites). Looser = more creative latitude; tighter = stays closer to the raw look.')}
          <div className="cs-cfg-group">
            <div className="cs-cfg-grouphd">Self-assessment guard</div>
            <div className="cs-cfg-note">
              Master switch for the natural-drift guard above. Off = the critique loop reverts to score-only (no measured pull-back toward the neutral decode).
            </div>
            <label className="cs-cfg-field cs-cfg-field--check">
              <input
                type="checkbox"
                checked={draft.selfAssess?.enabled !== false}
                onChange={(e) => setVal('selfAssess', 'enabled', e.target.checked)}
              />
              <span className="cs-cfg-label">Measure + enforce drift from the natural baseline</span>
            </label>
          </div>
          <div className="cs-cfg-group">
            <div className="cs-cfg-grouphd">Prep-once-to-EFS (experimental)</div>
            <div className="cs-cfg-note">
              On a first clip bake, download the multi-GB RED .RDC to shared storage <b>once</b> (a prep task) so every runner reuses it instead of each re-downloading it. Big first-bake win on wide fan-outs. Falls back automatically if prep fails. Toggle off here instantly if anything looks wrong.
            </div>
            <label className="cs-cfg-field cs-cfg-field--check">
              <input
                type="checkbox"
                checked={!!draft.pipeline?.stillsPrepToEfs}
                onChange={(e) => setVal('pipeline', 'stillsPrepToEfs', e.target.checked)}
              />
              <span className="cs-cfg-label">Download clip .RDC once to shared EFS</span>
            </label>
          </div>
        </div>
        <div className="cs-cfg-foot">
          {msg && <span className="cs-cfg-msg">{msg}</span>}
          <button className="cs-btn cs-btn-sm cs-btn-ghost" onClick={reset} disabled={busy}>Reset to defaults</button>
          <button className="cs-btn cs-btn-sm cs-btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save (live)'}</button>
        </div>
      </div>
    </div>
  );
}
